"use client";

import { useState } from "react";
import { SuperLink as Link } from "@/components/common/super-link";
import { LogoIconRemediumBlue } from "@/svg/logo-icon-remedium-blue";
import { cn } from "@/lib/utils";
import { RoadmapNavigationMilestone } from "./roadmap-navigation-milestone";
import { createPath } from "@/service/routing/routing";
import { ChevronLeftIcon, ListIcon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/ui/drawer";

export const RoadmapNavigation = ({
  milestones,
  specialization,
  activeStepId,
  accessToken,
  previewPathId,
}: {
  milestones: RoadmapMilestoneVM[];
  specialization: RoadmapVM;
  activeStepId?: number;
  accessToken?: string | string[];
  previewPathId?: number | null;
}) => {
  const tokenString = Array.isArray(accessToken) ? accessToken[0] : accessToken;
  const tokenQuery = tokenString ? `?access-token=${tokenString}` : "";
  const paths = milestones.flatMap((milestone) => milestone.paths);
  const steps = paths.flatMap((path) => path.steps);

  const activeStep = steps.find((s) => s.id === activeStepId);
  const activePathId =
    paths.find((p) => p.steps.some((s) => s.id === activeStepId))?.id ?? null;

  const activeMilestoneIndex = milestones.findIndex((m) =>
    m.paths.some((p) => p.steps.some((s) => s.id === activeStepId)),
  );
  const previewMilestoneIndex =
    previewPathId != null
      ? milestones.findIndex((m) => m.paths.some((p) => p.id === previewPathId))
      : -1;

  const initialMilestones = new Set<number>();
  if (activeMilestoneIndex !== -1) initialMilestones.add(activeMilestoneIndex);
  if (previewMilestoneIndex !== -1)
    initialMilestones.add(previewMilestoneIndex);
  if (initialMilestones.size === 0) initialMilestones.add(0);

  const [expandedMilestones, setExpandedMilestones] =
    useState<Set<number>>(initialMilestones);

  const activePathIndex =
    activePathId !== null ? paths.findIndex((p) => p.id === activePathId) : -1;
  const previewPathIndex =
    previewPathId != null ? paths.findIndex((p) => p.id === previewPathId) : -1;

  const initialPaths = new Set<number>();
  if (activePathIndex !== -1) initialPaths.add(activePathIndex);
  if (previewPathIndex !== -1) initialPaths.add(previewPathIndex);
  if (initialPaths.size === 0) initialPaths.add(0);

  const [expandedPaths, setExpandedPaths] = useState<Set<number>>(initialPaths);

  const toggleMilestone = (milestoneKey: number) => {
    setExpandedMilestones((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(milestoneKey)) {
        newSet.delete(milestoneKey);
      } else {
        newSet.add(milestoneKey);
      }
      return newSet;
    });
  };

  const togglePath = (index: number) => {
    setExpandedPaths((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <>
      {/* Mobile Version */}
      <div className="fixed top-0 left-0 z-10000 flex w-full items-center justify-between gap-4 border-b bg-white p-2 xl:hidden print:hidden">
        <Button
          variant="ghost"
          size="icon"
          asChild={true}
          className="min-w-[40px]"
        >
          <Link
            href={
              createPath("roadmap.show", {
                slug: specialization.slug,
              }) + tokenQuery
            }
          >
            <ChevronLeftIcon size={16} />
          </Link>
        </Button>
        <div className={"max-w-[70%] truncate text-sm font-semibold"}>
          {activeStepId ? activeStep?.path?.title : ""}
        </div>
        <Drawer direction={"bottom"}>
          <DrawerTrigger>
            <Button variant="outline" size="icon" className="min-w-[40px]">
              <ListIcon size={16} />
            </Button>
          </DrawerTrigger>
          <DrawerContent className={"font-inter -m-4 p-4"}>
            <div className="sidebar-scrollbar h-full overflow-y-auto px-0 pb-10">
              {milestones.map((milestone, index) => {
                const isMilestoneExpanded = expandedMilestones.has(index);

                return (
                  <RoadmapNavigationMilestone
                    key={index}
                    milestoneKey={index}
                    milestonePaths={milestone.paths}
                    isExpanded={isMilestoneExpanded}
                    onToggle={toggleMilestone}
                    paths={paths}
                    expandedPaths={expandedPaths}
                    onTogglePath={togglePath}
                    activePathId={activePathId}
                    activeStep={activeStep}
                    specialization={specialization}
                    isFirst={index === 0}
                    tokenQuery={tokenQuery}
                    previewPathId={previewPathId}
                  />
                );
              })}
            </div>
            <DrawerClose />
          </DrawerContent>
        </Drawer>
      </div>

      {/* Desktop Version - xl and above */}
      <div className="hidden max-h-[100vh] w-[360px] min-w-[360px] flex-1 bg-[#fafafa] xl:block">
        <div className="sticky top-0 mb-0 pb-4">
          <div className="flex items-center gap-3 border-b px-5 py-4">
            <Link
              className="flex items-center gap-3"
              href={
                createPath("roadmap.show", {
                  slug: specialization.slug,
                }) + tokenQuery
              }
            >
              <div className={cn("group relative")}>
                <div
                  className={
                    "bg-remedium-blue/5 group-hover:bg-remedium-blue/10 absolute -top-1 -left-1 z-1 h-[calc(100%+8px)] w-[calc(100%+8px)] scale-x-105 rounded-md transition-all group-hover:scale-y-105"
                  }
                ></div>
                <div className={"relative z-2"}>
                  <LogoIconRemediumBlue />
                </div>
              </div>
              <span className="text-[14px] font-semibold text-zinc-500">
                Wróć do "{specialization.title}"
              </span>
            </Link>
          </div>
        </div>

        {/* Main Roadmap Area - Desktop */}
        <div className="sidebar-scrollbar max-h-[calc(100vh-73px)] overflow-y-auto px-0 pb-10">
          {milestones.map((milestone, index) => {
            const isMilestoneExpanded = expandedMilestones.has(index);

            return (
              <RoadmapNavigationMilestone
                key={index}
                milestoneKey={index}
                milestonePaths={milestone.paths}
                isExpanded={isMilestoneExpanded}
                onToggle={toggleMilestone}
                paths={paths}
                expandedPaths={expandedPaths}
                onTogglePath={togglePath}
                activePathId={activePathId}
                activeStep={activeStep}
                specialization={specialization}
                isFirst={index === 0}
                tokenQuery={tokenQuery}
                previewPathId={previewPathId}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
